import {
  Button,
  Divider,
  Grid,
  Heading,
  Icon,
  IconNames,
  Link,
  Message,
  Text,
  useIsDesignLanguage,
  DesignLanguage,
  useDesignLanguagePerClass,
  useModal,
} from '@afterpaytouch/core'
import { ErrorCode, PortalApiError } from '@afterpaytouch/portal-api'
import clsx from 'clsx'
import React, { FunctionComponent, ReactNode } from 'react'
import Image from 'next/image'
import { Money } from '../'
import { Trans } from '../../utils/convergence'
import { app, BASE_PATH } from '../../env'
import { useAmplitude } from 'react-amplitude-hooks'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { APPLE_WALLET_PAYMENT_OFFER_RESULT_URL } from '../../flow/apple-paylater/constants'
import { CheckoutError, checkoutErrors, IdentityVerificationTerminalErrorModalKey, isTerminalErrorCode, TerminalErrorCode } from '../../model'
import {
  useApplePayEntryData,
  useCheckoutState,
  useCLRiskDeclineError,
  useConsumerLendingEligible,
  useIsConsumerLendingOrder,
  useIsAppleRetailOrder,
  useCountry,
  useIsAfterpayAppGiftCardFlow,
  useIsAfterpayFinanceFeeEligible,
  useIsApplePaylater,
  useIsAuNonAppleRetailCheckout,
  useIsOrderAmountExceedsSpendCap,
  useIsRecoverableFlow,
  useMerchantName,
  useRedirectUrl,
} from '../../state'
import styles from './styles.module.css'
import { CloseCheckout } from '../CloseCheckout'
import { TERMINAL_ERROR_CODE_IMAGE_MAP, getLinkAmpEvent, isAlternativeOptionTypeArray, shouldHideDescription } from './helper'
import { useCloseCheckout } from '../../utils/close'
import { isValidRedirectUrl } from '../../utils/redirectUrl'
import { IdentityVerificationTerminalErrorModal } from '../IdentityVerificationTerminalErrorModal'
import { useTranslation } from '../../utils/convergence'
import { isNullOrUndefined, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { Country } from '@afterpay/types'
import { Illustration } from '../Illustration'
import { useCompleteCheckoutJourneyOnTerminalError } from '../../integrations/userJourney'

interface AlternativeTitleProps {
  children?: ReactNode
  showInArcade?: boolean
}

export const AlternativeTitle: FunctionComponent<AlternativeTitleProps> = ({ children, showInArcade = false }): ReactNode => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  if (isArcadeDesignLanguage && !showInArcade) {
    return null
  }

  return (
    <div className={styles.alternativeTitle}>
      <Heading size='L'>{children}</Heading>
    </div>
  )
}

export const getAltTitleContext = (errorKey: string, isRecoverableFlow: boolean, isConsumerLendingEligible: boolean): string => {
  const isDeclinePCL = errorKey === TerminalErrorCode.InvalidAmount
  const isStateRestrictions = errorKey === TerminalErrorCode.StateRestrictionsPCL
  const isCreditFreezeDeclinedPCL = errorKey === TerminalErrorCode.CreditFreezeDeclinedPCL
  const isComplianceDeclineBureau = errorKey === TerminalErrorCode.ComplianceDeclineBureau
  const isKycRefreshFlow = errorKey === TerminalErrorCode.KYCRefreshPending
  switch (true) {
    case isConsumerLendingEligible || isDeclinePCL || isStateRestrictions || isCreditFreezeDeclinedPCL:
      return 'declinePCL'
    case isComplianceDeclineBureau:
      return 'complianceDeclineBureau'
    case isRecoverableFlow:
      return 'recoverable'
    case isKycRefreshFlow:
      return 'KYCRefreshPending'
    default:
      return ''
  }
}

const FormattedAmount: FunctionComponent<{ value: string }> = ({ value }) => (
  <Text size='M' renderAs='span' color='Black'>
    {value}
  </Text>
)

export const TerminalError: FunctionComponent<CheckoutError> = ({ error, errorCode, extra, showBackToStore = true }) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { logEvent: logAmplitudeEvent } = useAmplitude()
  const { t } = useTranslation()
  const PORTAL_URL: string = app.PORTAL_URL
  const { checkout, started } = useCheckoutState()
  const { error: clError } = useCLRiskDeclineError()
  const country = useCountry()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const showClose = showBackToStore && checkout !== undefined
  let errorKey = isTerminalErrorCode(error) ? error : checkoutErrors[clError?.errorCode as string] ?? TerminalErrorCode.Default
  if (errorKey === TerminalErrorCode.PayMonthlyOrderAmountTooHigh && errorCode === ErrorCode.OrderAmountTooHigh) {
    errorKey = TerminalErrorCode.OrderAmountTooHigh
  }
  const installmentsCount = started?.paymentSchedules?.length ?? 4
  const merchantName = useMerchantName()
  const { isOrderAmountExceedsSpendCap, isOrderAmountExceedsSpendCapAndNotEnabled } = useIsOrderAmountExceedsSpendCap(errorKey)
  if (isOrderAmountExceedsSpendCapAndNotEnabled) {
    errorKey = TerminalErrorCode.Default
  }
  const errorImage = TERMINAL_ERROR_CODE_IMAGE_MAP[errorKey]
  const isPayMonthlyOrderAmountTooHigh = errorKey === TerminalErrorCode.PayMonthlyOrderAmountTooHigh
  const isPayMonthlyOverMerchantMaximum = isPayMonthlyOrderAmountTooHigh && errorCode === ErrorCode.InvalidAmount
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const designLanguageClass = useDesignLanguagePerClass(styles)
  const isRecoverableFlow = useIsRecoverableFlow()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const isAppleRetailOrder = useIsAppleRetailOrder()
  const isConsumerLendingDecline = isConsumerLendingOrder || isAppleRetailOrder
  if (country === Country.AU && isConsumerLendingDecline) {
    if (errorKey === TerminalErrorCode.RiskDeclined) {
      errorKey = TerminalErrorCode.RiskDeclinedPCL
    } else if (errorKey === TerminalErrorCode.AmountTooHigh) {
      errorKey = TerminalErrorCode.AmountTooHighDeclinePCL
    }
  }
  const isGbCountry = country === Country.GB
  const declinePCLErrorKeys = [TerminalErrorCode.InvalidAmount, TerminalErrorCode.AmountTooHighDeclinePCL]
  const isDeclinePCL = isConsumerLendingEligible || declinePCLErrorKeys.includes(errorKey)
  const isUkRegulatedOverdueError =
    isGbCountry && (errorKey === TerminalErrorCode.OverduePayments || errorKey === TerminalErrorCode.OverduePaymentsCheckoutPaymentEligible)
  const showUkRegulatedOverdueSectionHeadings = isArcadeDesignLanguage && isUkRegulatedOverdueError

  const altTitleContext = getAltTitleContext(errorKey, isRecoverableFlow, isConsumerLendingEligible)
  const failureCallbackUrl = checkout?.merchant?.failureCallbackUrl
  const close = useCloseCheckout({ redirectUrlOverride: failureCallbackUrl })
  const testNameSpace = errorCode ?? clError?.errorCode ?? errorKey
  const isAmountTooHigh = errorKey === TerminalErrorCode.AmountTooHighDeclinePCL || errorKey === TerminalErrorCode.AmountTooHigh
  const isAuNonAppleRetailCheckout = useIsAuNonAppleRetailCheckout()
  const isPayMonthlyOnly = extra?.payMonthlyOnly === true || String(extra?.payMonthlyOnly) === 'true'
  const isAuPayMonthlyDecline =
    country === Country.AU &&
    (isConsumerLendingDecline || (isAuNonAppleRetailCheckout && isPayMonthlyOnly)) &&
    [TerminalErrorCode.RiskDeclinedPCL, TerminalErrorCode.AmountTooHighDeclinePCL, TerminalErrorCode.ExcessiveLateFeeAmount].includes(errorKey)
  const showAuPayMonthlyOptionHeading =
    isAuNonAppleRetailCheckout && isPayMonthlyOnly && [TerminalErrorCode.RiskDeclinedPCL, TerminalErrorCode.ExcessiveLateFeeAmount].includes(errorKey)
  const showOptionHeading = showUkRegulatedOverdueSectionHeadings || showAuPayMonthlyOptionHeading
  const isAuPayMonthlyAmountTooHigh = isAuNonAppleRetailCheckout && isAuPayMonthlyDecline && errorKey === TerminalErrorCode.AmountTooHighDeclinePCL

  const headingIconExclusionErrorCode = [TerminalErrorCode.Default, TerminalErrorCode.PayMonthlyOrderAmountTooHigh]
  const isHeadingIconExcluded = headingIconExclusionErrorCode.includes(errorKey)
  const showMintBar = !isOrderAmountExceedsSpendCap
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const applePayEntryData = useApplePayEntryData()
  const redirectUrl = useRedirectUrl()
  const isApplePaylater = useIsApplePaylater()
  const completeCheckoutJourneyOnTerminalError = useCompleteCheckoutJourneyOnTerminalError()

  const { showModal: showInformationModal } = useModal(IdentityVerificationTerminalErrorModalKey)

  const renderLimits = (): ReactNode => {
    if (isNullOrUndefined(extra?.availableToSpend)) {
      return null
    }

    if (isOrderAmountExceedsSpendCapAndNotEnabled) {
      return null
    }

    const limitMoney = extra?.availableToSpend

    if (isArcadeDesignLanguage) {
      return (
        <div className={designLanguageClass.limitsWrapper} data-testid='terminal-error-limits'>
          <Grid verticalGutter={{ sm: 1, md: 1, lg: 1 }}>
            <Grid.Item sm={12}>
              <div className={designLanguageClass.limitsRowItem}>
                <Text>{t('terminalError:limits.orderTotal')}</Text>
                <Money size={'M'} value={checkout?.amount} />
              </div>
            </Grid.Item>
            <Grid.Item sm={12}>
              <div className={designLanguageClass.limitsRowItem}>
                <Text>{isGbCountry ? t('terminalError:limits.availableToSpend', { context: 'regulated' }) : t('terminalError:limits.availableToSpend')}</Text>
                <Money size={'M'} value={limitMoney} />
              </div>
            </Grid.Item>
          </Grid>
        </div>
      )
    }

    return (
      <div className={styles.availableToSpendWrapper} data-testid='terminal-error'>
        <Money mintBar={showMintBar} value={limitMoney} size='XXXXL' bold />
        <Text bold={isOrderAmountExceedsSpendCap}>
          {isGbCountry ? t('terminalError:availableToSpend', { context: 'regulated' }) : t('terminalError:availableToSpend')}
        </Text>
        <Divider kind='Hairline' />
      </div>
    )
  }

  const renderAuPayMonthlyLimits = (): ReactNode => {
    if (!isAuPayMonthlyAmountTooHigh || isNullOrUndefined(extra?.maximumOrderValue)) {
      return null
    }

    return (
      <div className={designLanguageClass.limitsWrapper} data-testid='terminal-error-pay-monthly-limits'>
        <Grid verticalGutter={{ sm: 1, md: 1, lg: 1 }}>
          <Grid.Item sm={12}>
            <div className={designLanguageClass.limitsRowItem}>
              <Text>{t('terminalError:limits.orderTotal')}</Text>
              <Money size={'M'} value={checkout?.amount} />
            </div>
          </Grid.Item>
          <Grid.Item sm={12}>
            <div className={designLanguageClass.limitsRowItem}>
              <Text>{t('terminalError:limits.payMonthlyAvailableToSpend')}</Text>
              <Text size={'M'} testNameSpace={`${testNameSpace}-pay-monthly-limit`}>
                {extra.maximumOrderValue}
              </Text>
            </div>
          </Grid.Item>
        </Grid>
      </div>
    )
  }

  const renderHeadingIcon = (): ReactNode => {
    if (!isArcadeDesignLanguage || isHeadingIconExcluded || showUkRegulatedOverdueSectionHeadings) {
      return null
    }
    return (
      <div className={styles.headingIconWrapper} data-testid='terminal-error-heading-icon'>
        <Icon name='AlertFill' color='Fire' size='Large' />
      </div>
    )
  }

  const geti18nDescContext = (): string => {
    const recoverableFlowContext = isRecoverableFlow ? 'recoverable' : ''

    if (isAuPayMonthlyDecline) {
      return 'clAuDeclined'
    }

    if (isPayMonthlyOverMerchantMaximum) {
      return 'merchantMaximum'
    }

    if (errorKey === TerminalErrorCode.AmountTooHighDeclinePCL && typeof extra?.maximumOrderValue === 'undefined') {
      return 'generic'
    }

    if ((errorKey === TerminalErrorCode.AffordabilityDecline || errorKey === TerminalErrorCode.CreditCheckFailure) && extra?.isInitialLimit === false) {
      return 'existingCustomer'
    }

    if (extra?.payMonthlyOnly !== undefined && extra?.payMonthlyOnly) {
      return 'payMonthlyOnly'
    }

    if (isUkRegulatedOverdueError) {
      return 'regulated'
    }

    if (isGbCountry && isRecoverableFlow && errorKey === TerminalErrorCode.AmountTooHigh) {
      return 'recoverableRegulated'
    }

    return recoverableFlowContext
  }

  const getDescriptionContext = (): string => {
    if (isAuNonAppleRetailCheckout && (errorKey === TerminalErrorCode.AccountClosed || errorKey === TerminalErrorCode.AccountDisabled)) {
      return 'auPm'
    }

    return geti18nDescContext()
  }

  const getAuPmDeclineContext = (): string => {
    const base = geti18nDescContext()
    return isAuNonAppleRetailCheckout && base === 'clAuDeclined' ? 'clAuPmDeclined' : base
  }

  const i18nDescription = `terminalError:${errorKey}.description`

  const renderDescription = (): JSX.Element | null => {
    const context = getDescriptionContext()

    const description = (
      <Trans
        i18nKey={i18nDescription}
        values={{ ...extra, maximumOrderValue: extra?.maximumOrderValue, numberOrdersToPay: extra?.numberOrdersToPay, interval: installmentsCount }}
        components={{
          alternativeTitle: <AlternativeTitle />,
          contact: <Link key='description-link' target='_blank' kind='Underline' href={t('common:contactUrl')} />,
          minimumOrderValue: <Money size='M' value={extra?.minimumOrderValue} />,
          merchantMaximumOrderValue:
            errorCode === ErrorCode.InvalidAmount && !isNullOrUndefined(extra?.maximumOrderValue) ? (
              <FormattedAmount value={extra.maximumOrderValue} />
            ) : (
              <Money size='M' value={checkout?.maximumOrderValue} />
            ),
          bold: <strong />,
          br: <br />,
          helpLandingUrl: <Link key='description-link' target='_blank' kind='Underline' href={t('common:helpLandingUrl')} />,
          accountLogin: <Link key='account-login-link' target='_blank' kind='Underline' bold href={t('common:consumerUrl')} />,
          payMonthlyApp: (
            <Link
              key='pay-monthly-app-link'
              target='_blank'
              kind='Underline'
              href={t('common:openMobileAppUrl')}
              onClick={() => logAmplitudeEvent(TrackingEvent.CHECKOUT_PAY_MONTHLY_APP_LINK_CLICKED, () => ({}))}
            />
          ),
        }}
        context={context}
        defaults={' '}
      />
    )

    if (isArcadeDesignLanguage) {
      return (
        <Text renderAs='div' testNameSpace={errorCode} color={showUkRegulatedOverdueSectionHeadings ? 'Subtle' : undefined}>
          {description}
        </Text>
      )
    }

    const hideDescription = shouldHideDescription({ errorKey, isOrderAmountExceedsSpendCap, country })
    if (hideDescription) {
      return null
    }

    return (
      <Text renderAs='div' testNameSpace={testNameSpace}>
        {description}
      </Text>
    )
  }

  /* eslint-disable @afterpay/i18n-only-static-keys, @afterpay/i18n-no-dynamic-context */
  const renderDescriptionHeading = (): ReactNode => {
    if (!showUkRegulatedOverdueSectionHeadings) {
      return null
    }

    return <AlternativeTitle showInArcade>{t(`terminalError:${errorKey}.descriptionHeading`, { context: geti18nDescContext() })}</AlternativeTitle>
  }

  const renderAlternativeOptions = (): JSX.Element | null => {
    if (isAuPayMonthlyAmountTooHigh) {
      return null
    }
    const descContext = getAuPmDeclineContext()
    const optionContext = descContext === 'recoverableRegulated' ? 'recoverable' : descContext
    const option = t(`terminalError:${errorKey}.option`, {
      returnObjects: true,
      defaultValue: [],
      context: optionContext,
    })
    if (!isAlternativeOptionTypeArray(option)) {
      return null
    }

    const maximumOrderValue = extra?.maximumOrderValue ?? (clError as PortalApiError)?.errorInfo?.maximum_order_value

    const mkIcon = (index: number, icon?: IconNames): JSX.Element => (
      <>
        {icon !== undefined && (
          <div className={styles.iconWrapper} key={`alternative-option-icon-${index}`}>
            <Icon name={icon} size={showUkRegulatedOverdueSectionHeadings ? 'Medium' : 'Small'} />
          </div>
        )}
      </>
    )

    const mkLink = (index: number, url?: string): JSX.Element => {
      let validUrl = url
      if (isOrderAmountExceedsSpendCap) {
        validUrl = PORTAL_URL
      }
      const { event, options } = getLinkAmpEvent(errorKey, validUrl)

      return validUrl !== undefined ? (
        <Link
          key={`alternative-option-link-${index}`}
          target='_blank'
          kind='Underline'
          color={showUkRegulatedOverdueSectionHeadings ? 'Standard' : undefined}
          href={validUrl}
          onClick={() => logEvent(event, options)}
        />
      ) : (
        <></>
      )
    }

    const mkCloseCheckout = (index: number): JSX.Element => {
      return <Link key={`alternative-option-link-${index}`} kind='Underline' onClick={() => void close()} />
    }

    const alternativeTitleKey = isOrderAmountExceedsSpendCap ? 'alternativeTitle_recoverable' : 'alternativeTitle'
    const alternativeTitle = showOptionHeading
      ? t(`terminalError:${errorKey}.optionHeading`, { context: getAuPmDeclineContext() })
      : t(`terminalError:${alternativeTitleKey}`, { context: altTitleContext })

    return (
      <>
        <AlternativeTitle showInArcade={showOptionHeading}>{alternativeTitle}</AlternativeTitle>
        <ul className={styles.alternativeOptionList}>
          {option.map(({ title, url, icon }, index) => (
            <li
              key={`alternative-option-${index}`}
              className={clsx(
                styles.alternativeOption,
                icon !== undefined && styles.alternativeOptionWithIcon,
                showUkRegulatedOverdueSectionHeadings && icon !== undefined && styles.regulatedOverdueOptionWithIcon
              )}
            >
              {mkIcon(index, icon)}
              <Text renderAs='div' testNameSpace={`alt-option-${index}`} color={showUkRegulatedOverdueSectionHeadings ? 'Subtle' : undefined}>
                <Trans
                  key={`terminal-error-alternative-option-${index}`}
                  i18nKey={title}
                  values={{ interval: installmentsCount, merchantName, maximumOrderValue }}
                  components={{ a: mkLink(index, url), close: mkCloseCheckout(index) }}
                />
              </Text>
            </li>
          ))}
        </ul>
      </>
    )
  }

  const renderMoreInformation = (): ReactNode => {
    if (errorKey === TerminalErrorCode.IdentityVerificationDeclinedPCL) {
      return (
        <>
          <IdentityVerificationTerminalErrorModal />
          <Message
            kind='Basic'
            iconName='Info'
            heading={t(`terminalError:identityVerificationDeclinedPCL.information.heading`)}
            description={t(`terminalError:identityVerificationDeclinedPCL.information.message`)}
            link={
              <div className={styles.informationTextContainer}>
                <Link size='XS' bold kind='Underline' onClick={() => showInformationModal()}>
                  {t(`terminalError:identityVerificationDeclinedPCL.information.link`)}
                </Link>
              </div>
            }
          />
        </>
      )
    }
    if (errorKey === TerminalErrorCode.KYCRefreshPending) {
      return <Text>{t(`terminalError:${errorKey}.detail`)}</Text>
    }
    return null
  }

  const renderOrderAmountExceedsSpendCapAlternativeOptions = (): ReactNode => (
    <>
      <AlternativeTitle>{t('terminalError:alternativeTitle_meaning', { context: altTitleContext })}</AlternativeTitle>
      <Text renderAs='div'>
        <Trans i18nKey={t(`terminalError:${errorKey}:meaning_description`, { context: altTitleContext })} />
      </Text>
    </>
  )

  const renderSubheading = (): ReactNode => {
    if (isDeclinePCL && isAmountTooHigh && extra?.maximumOrderValue && !isAuPayMonthlyDecline) {
      const maximumOrderValue = extra?.maximumOrderValue.split('.')[0]
      return (
        <>
          <div className={styles.underlineContainer}>
            <Text size='XXXXL' bold>
              {maximumOrderValue}
            </Text>
            <div className={designLanguageClass.underline}></div>
          </div>
          <div className={styles.textContainer}>
            <Text testNameSpace={`${testNameSpace}-subheading`}>{t(`terminalError:${errorKey}.subheading`)}</Text>
          </div>
          <Divider kind='Hairline' />
        </>
      )
    }
    return null
  }

  const showHelpCenterButton = errorKey === TerminalErrorCode.IdentityVerificationFailedManual

  const helpCenterButton = (): ReactNode => {
    const openUrl = async (): Promise<void> => {
      await completeCheckoutJourneyOnTerminalError()
      const url = t('common:identityHelpUrl')
      window.open(url, '_self', 'noopener')
    }

    return (
      <Button kind='Secondary' padding='Fluid' onClick={() => void openUrl()} testNameSpace={testNameSpace}>
        {t(`terminalError:${errorKey}.cta`)}
      </Button>
    )
  }
  /* eslint-enable @afterpay/i18n-only-static-keys, @afterpay/i18n-no-dynamic-context */

  const renderCta = (): ReactNode => {
    if (isAfterpayAppGiftCardFlow || isAFF) {
      return null
    }

    return (
      <CloseCheckout kind='Secondary' padding='Fluid' testNameSpace='terminal-error' redirectUrlOverride={failureCallbackUrl}>
        {t('terminalError:backToStore')}
      </CloseCheckout>
    )
  }

  const renderApplePaylaterCta = (): ReactNode => {
    const handleDone = async (): Promise<void> => {
      await completeCheckoutJourneyOnTerminalError()
      const isRedirectUrlValid = isValidRedirectUrl(redirectUrl)
      if (redirectUrl && !isRedirectUrlValid) {
        logEvent(TrackingEvent.CHECKOUT_FATAL_ERROR, { error: 'Invalid Apple terminal-error checkout redirect URL' })
      }
      if (isRedirectUrlValid) {
        window.location.href = redirectUrl
      } else if (valueIsNotNullOrUndefined(applePayEntryData?.sessionIdentifier)) {
        window.location.href = `${APPLE_WALLET_PAYMENT_OFFER_RESULT_URL}?sessionIdentifier=${applePayEntryData.sessionIdentifier}`
      }
    }

    return (
      <Button kind='Secondary' padding='Fluid' onClick={() => void handleDone()} testNameSpace='terminal-error-done'>
        {t('terminalError:Done')}
      </Button>
    )
  }

  return (
    <>
      <Grid>
        <div>
          {isPayMonthlyOrderAmountTooHigh && (
            <div className={styles.heroImage} data-testid='terminal-error-hero-image'>
              <Image
                src={[BASE_PATH, 'images/pm-facility-hero.svg'].join('/')}
                alt='Pay Monthly'
                width={390}
                height={216}
                loading='eager'
                style={{ aspectRatio: '390 / 216', height: 'auto', maxWidth: '100%' }}
              />
            </div>
          )}
          {errorImage !== undefined && (
            <div className={styles.heroImage}>
              <Illustration name={errorImage} width={327} height={250} testId='terminal-error-hero-image' />
            </div>
          )}
          {renderHeadingIcon()}
          <Heading size='XXL' testNameSpace={testNameSpace}>
            <Trans
              i18nKey={[`terminalError:${errorKey}.heading`, 'terminalError:default.heading']}
              context={getAuPmDeclineContext()}
              values={{ maximumOrderValue: extra?.maximumOrderValue }}
              components={{
                minimumOrderValue: <Money size='XXL' bold value={extra?.minimumOrderValue} />,
              }}
            />
          </Heading>
          {!isAuPayMonthlyAmountTooHigh && !isPayMonthlyOrderAmountTooHigh && renderLimits()}
          {renderSubheading()}
          <div data-testid='terminal-error-description-wrapper' className={designLanguageClass.descriptionWrapper}>
            {renderDescriptionHeading()}
            {renderDescription()}
            {renderAuPayMonthlyLimits()}
            {isOrderAmountExceedsSpendCap && renderOrderAmountExceedsSpendCapAlternativeOptions()}
            {renderAlternativeOptions()}
            {renderMoreInformation()}
          </div>
        </div>
      </Grid>
      {isApplePaylater && (
        <Grid>
          <Grid.Item>{renderApplePaylaterCta()}</Grid.Item>
        </Grid>
      )}
      {!isApplePaylater && showClose && (
        <Grid>
          <Grid.Item>{showHelpCenterButton ? helpCenterButton() : renderCta()}</Grid.Item>
        </Grid>
      )}
    </>
  )
}
